// TPCUnifiedBackdrop.js
import React, { useEffect, useMemo, useRef } from "react";
import FloatingOrbs from "./FloatingOrbs";
import "./styles/TPCUnifiedBackdrop.css";

/**
 * Unified Backdrop
 * - bg gradients + grid + physics orbs + edge glow
 * - Stars (canvas)
 *   - Depth-based parallax on scroll
 *   - Subtle twinkle
 *   - Some stars have a warm golden glow
 *   - IMPORTANT: Stars are kept BEHIND content (no overlay)
 *
 * Key fix:
 * - Stars are NOT position:fixed anymore.
 * - They are position:absolute inside the component (so stacking works),
 *   then we "pin" them to the viewport using a transform translateY(scroll).
 */

/* ================================
   Stars (Canvas)
   ================================ */

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand = (a, b) => a + Math.random() * (b - a);
const wrapMod = (n, m) => {
  const r = n % m;
  return r < 0 ? r + m : r;
};

function buildStars(w, h) {
  const area = w * h;

  // Density tuned for “space” feel (cap for perf)
  const count = clamp(Math.floor(area / 6500), 90, 260);

  return new Array(count).fill(0).map(() => {
    const depth = rand(0.12, 1.0); // 0.12 far, 1 near

    // A few “hero” stars
    const hero = Math.random() < 0.07;

    // Some golden stars (requested)
    const gold = Math.random() < 0.11; // ~11% warm glow stars
    const goldStrength = gold ? rand(0.65, 1.2) : 0;

    const baseR = rand(0.35, 1.25) * (0.55 + depth * 0.65);
    const baseA = rand(0.14, 0.72) * (0.45 + depth * 0.7);

    const r = hero ? baseR * rand(1.35, 2.15) : baseR;
    const a = hero ? baseA * rand(1.05, 1.25) : baseA;

    // Color temperature variance (non-gold stars)
    const cool = !gold && Math.random() < 0.35;
    const warm = !gold && !cool && Math.random() < 0.18;

    // Gold stars get a more consistent warm palette
    const col = gold
      ? {
          r: rand(245, 255),
          g: rand(200, 230),
          b: rand(140, 175),
        }
      : cool
        ? { r: rand(210, 235), g: rand(225, 245), b: rand(245, 255) }
        : warm
          ? { r: rand(245, 255), g: rand(228, 245), b: rand(200, 230) }
          : { r: rand(235, 255), g: rand(235, 255), b: rand(235, 255) };

    return {
      x: rand(0, w),
      y: rand(0, h),
      depth,
      r,
      a,
      hero,
      gold,
      goldStrength,
      rgb: `rgb(${col.r.toFixed(0)},${col.g.toFixed(0)},${col.b.toFixed(0)})`,
      twPhase: rand(0, Math.PI * 2),
      twSpeed: rand(0.55, 1.25),
    };
  });
}

function StarsLayer({ className = "" }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }, []);

  const stRef = useRef({
    ready: false,
    w: 0,
    h: 0,
    dpr: 1,
    ctx: null,
    stars: [],

    // scroll-parallax
    scrollY: 0,
    targetScrollY: 0,
    lastScrollY: 0,
    scrollEnergy: 0,

    // anchor for "pseudo-fixed"
    rootDocTop: 0,

    lastT: 0,
  });

  // Setup viewport-sized canvas (performance-safe)
  useEffect(() => {
    if (reduceMotion) return;

    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const st = stRef.current;

    const computeRootDocTop = () => {
      const root = wrap.closest?.(".tpc-unified");
      if (!root) return 0;
      const rect = root.getBoundingClientRect();
      return (window.scrollY || 0) + rect.top;
    };

    const resize = () => {
      const w = Math.max(1, Math.floor(window.innerWidth || 1));
      const h = Math.max(1, Math.floor(window.innerHeight || 1));
      const dpr = clamp(window.devicePixelRatio || 1, 1, 2);

      st.w = w;
      st.h = h;
      st.dpr = dpr;

      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const ctx = canvas.getContext("2d", { alpha: true });
      if (!ctx) return;

      st.ctx = ctx;
      // draw in CSS pixels
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // rebuild stars on resize
      st.stars = buildStars(w, h);

      // update root docTop used for pseudo-fixed transform
      st.rootDocTop = computeRootDocTop();

      if (!st.ready) {
        const y = window.scrollY || 0;
        st.scrollY = y;
        st.targetScrollY = y;
        st.lastScrollY = y;
        st.lastT = performance.now();
        st.ready = true;
      } else {
        st.targetScrollY = window.scrollY || 0;
      }

      // apply immediate pin transform
      const y = window.scrollY || 0;
      wrap.style.transform = `translate3d(0, ${y - st.rootDocTop}px, 0)`;
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [reduceMotion]);

  // Scroll handler -> parallax target + energy + immediate "pin"
  useEffect(() => {
    if (reduceMotion) return;

    const st = stRef.current;

    const onScroll = () => {
      const wrap = wrapRef.current;

      const y = window.scrollY || 0;
      const dy = y - st.lastScrollY;
      st.lastScrollY = y;

      st.targetScrollY = y;

      const abs = Math.abs(dy);
      if (abs >= 1) {
        const impulse = Math.min(120, abs) / 120;
        st.scrollEnergy = Math.min(1, st.scrollEnergy * 0.88 + impulse * 0.5);
      }

      // keep the canvas visually pinned to the viewport
      if (wrap) {
        wrap.style.transform = `translate3d(0, ${y - st.rootDocTop}px, 0)`;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [reduceMotion]);

  // RAF draw loop
  useEffect(() => {
    if (reduceMotion) return;

    const st = stRef.current;

    const tick = (t) => {
      rafRef.current = requestAnimationFrame(tick);
      if (!st.ready || !st.ctx) return;

      const ctx = st.ctx;
      const w = st.w;
      const h = st.h;
      if (w <= 0 || h <= 0) return;

      const dtMs = Math.min(34, t - st.lastT || 16.67);
      st.lastT = t;
      const dt = dtMs / 16.67;

      // smooth scroll target to avoid micro-jitter inside parallax math
      st.scrollY += (st.targetScrollY - st.scrollY) * (0.08 * dt);
      st.scrollEnergy *= Math.pow(0.92, dt);

      // Parallax offsets (depth-based)
      const worldY = st.scrollY * 0.18;
      const worldX = Math.sin(st.scrollY * 0.0022) * 44;

      ctx.clearRect(0, 0, w, h);

      // Additive-ish look feels more “cosmic”
      ctx.globalCompositeOperation = "lighter";

      for (let i = 0; i < st.stars.length; i++) {
        const s = st.stars[i];

        const px = s.x + worldX * s.depth;
        const py = s.y + worldY * s.depth;

        const x = wrapMod(px, w);
        const y = wrapMod(py, h);

        // twinkle
        const tw =
          0.74 +
          0.26 * Math.sin(t * 0.00125 * s.twSpeed + s.twPhase + s.depth * 1.35);

        // slight shimmer when scrolling
        const motionBoost = 1 + st.scrollEnergy * 0.12 * s.depth;

        const a = clamp(s.a * tw * motionBoost, 0, 1);

        if (s.gold) {
          // Gold glow: warm bloom + warm core + tiny white pin
          const glowCol = "rgb(255, 205, 130)";
          const warmCore = "rgb(255, 230, 190)";

          // outer glow
          ctx.fillStyle = glowCol;
          ctx.globalAlpha = a * (0.14 * s.goldStrength);
          ctx.beginPath();
          ctx.arc(x, y, s.r * (s.hero ? 4.1 : 3.1), 0, Math.PI * 2);
          ctx.fill();

          // mid glow
          ctx.globalAlpha = a * (0.26 * s.goldStrength);
          ctx.beginPath();
          ctx.arc(x, y, s.r * (s.hero ? 2.3 : 1.85), 0, Math.PI * 2);
          ctx.fill();

          // warm core
          ctx.fillStyle = warmCore;
          ctx.globalAlpha = a * 0.92;
          ctx.beginPath();
          ctx.arc(x, y, s.r * 1.05, 0, Math.PI * 2);
          ctx.fill();

          // crisp white pin
          ctx.fillStyle = "rgb(255,255,255)";
          ctx.globalAlpha = a * 0.75;
          ctx.beginPath();
          ctx.arc(x, y, Math.max(0.7, s.r * 0.55), 0, Math.PI * 2);
          ctx.fill();

          continue;
        }

        // Non-gold stars
        ctx.fillStyle = s.rgb;

        if (s.hero) {
          // soft bloom
          ctx.globalAlpha = a * 0.22;
          ctx.beginPath();
          ctx.arc(x, y, s.r * 2.2, 0, Math.PI * 2);
          ctx.fill();

          ctx.globalAlpha = a * 0.45;
          ctx.beginPath();
          ctx.arc(x, y, s.r * 1.25, 0, Math.PI * 2);
          ctx.fill();
        }

        // crisp core
        ctx.globalAlpha = a;
        ctx.beginPath();
        ctx.arc(x, y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [reduceMotion]);

  if (reduceMotion) return null;

  return (
    <div ref={wrapRef} className={className} aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}

/* ================================
   Backdrop Wrapper
   ================================ */

export default function TPCUnifiedBackdrop({ children, ariaLabel = "Bootcamp Journey" }) {
  const unifiedRef = useRef(null);

  const orbs = useMemo(
    () => [
      {
        id: "orb-1",
        size: 420,
        color: "rgba(255, 196, 92, 0.14)",
        dur: "16s",
        opacity: 0.6,
        delay: "-7s",
      },
      {
        id: "orb-2",
        size: 360,
        color: "rgba(110, 170, 255, 0.12)",
        dur: "18s",
        opacity: 0.55,
        delay: "-12s",
      },
      {
        id: "orb-3",
        size: 520,
        color: "rgba(92, 255, 220, 0.09)",
        dur: "20s",
        opacity: 0.42,
        delay: "-14s",
      },
      {
        id: "orb-4",
        size: 260,
        color: "rgba(190, 120, 255, 0.10)",
        dur: "15s",
        opacity: 0.4,
        delay: "-9s",
      },
      {
        id: "orb-5",
        size: 380,
        color: "rgba(255, 196, 92, 0.10)",
        dur: "17s",
        opacity: 0.35,
        delay: "-11s",
      },
      {
        id: "orb-6",
        size: 300,
        color: "rgba(110, 170, 255, 0.10)",
        dur: "16s",
        opacity: 0.28,
        delay: "-13s",
      },
      {
        id: "orb-7",
        size: 340,
        color: "rgba(92, 255, 220, 0.08)",
        dur: "19s",
        opacity: 0.26,
        delay: "-16s",
      },
    ],
    [],
  );

  return (
    <section
      ref={unifiedRef}
      className="tpc-unified"
      aria-label={ariaLabel}
    >
      {/* ✅ Stars layer (now properly behind everything) */}
      <StarsLayer className="tpc-unified__stars" />

      <div className="tpc-unified__bg" aria-hidden="true" />
      <div className="tpc-unified__grid" aria-hidden="true" />

      <FloatingOrbs className="tpc-unified__orbs" orbs={orbs} />

      <div className="tpc-unified__content">{children}</div>

      <div className="tpc-unified__edgeGlow" aria-hidden="true" />
    </section>
  );
}
